/* eslint-disable no-nested-ternary */
import {
  Controller,
  ControllerProps,
  Control,
  FieldError,
  useFormContext,
  FieldValues,
  UseControllerProps,
} from '@graphcommerce/react-hook-form'
import { i18n } from '@lingui/core'
import { TextField, TextFieldProps } from '@mui/material'
import { useEffect } from 'react'

export type TextFieldElementProps<T extends FieldValues = FieldValues> = Omit<
  TextFieldProps,
  'name' | 'defaultValue'
> & {
  validation?: UseControllerProps<T>['rules']
  parseError?: (error: FieldError) => string
  persianField?: boolean
} & UseControllerProps<T>

export function TextFieldElement<TFieldValues extends FieldValues>({
  validation = {},
  parseError,
  type,
  required,
  name,
  control,
  defaultValue,
  persianField,
  label,
  ...rest
}: TextFieldElementProps<TFieldValues>): JSX.Element {

  if (type === 'email' && !validation.pattern) {
    validation.pattern = {
      // eslint-disable-next-line no-useless-escape
      value:
        /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
      message: 'Please enter a valid email address',
    }
  }

  if (type === 'tel' && !validation.pattern) {
    validation.pattern = {
      // eslint-disable-next-line no-useless-escape
      value: /^(\+98|0)?9\d{9}$/,
      message: 'لطفاً یک شماره تلفن همراه معتبر وارد نمایید.',
    }
  }

  if (type === 'otp' && !validation.pattern) {
    validation.min = {
      // eslint-disable-next-line no-useless-escape
      value: 5,
      message: 'کد دارای ۵ رقم نیست.',
    }
  }



  const { formState: { errors, dirtyFields }, setError, trigger, setValue, clearErrors } = useFormContext();

  useEffect(() => {
    if (defaultValue) {
      setValue(name, defaultValue)
    }
  }, [defaultValue])
  return (
    <Controller
      name={name}
      control={control}
      rules={{
        ...validation,
        ...(required ? { required: 'واردکردن موارد ستاره‌دار الزامی است.' } : null),
        ...(persianField ? { pattern: { value: /^[\u0600-\u06FF\s]+$/, message: 'تنها استفاده از حروف فارسی مجاز است' } } : null),
      }}
      defaultValue={defaultValue}

      render={({ field: { value, onChange, onBlur, ref }, fieldState: { error, isDirty }, formState: { errors } }) => {
        return (
          <TextField
            {...rest}
            name={name}
            ref={ref}
            label={`${label}${required ? '*' : ''}`}
            value={value ?? ''}
            onChange={(ev) => {
              if (type === 'number') {
                onChange(
                  ev.target.value ? Number(ev.target.value) : ev.target.value,
                )
              }
              else if (type === 'otp') {
                onChange(
                  ev.target.value.slice(0, 5)
                )
              } else {
                onChange(
                  ev.target.value
                )
              }
            }}
            onBlur={e => {
              if (type === 'tel') {
                trigger(name)
              }

              if (persianField) {
                !e.target.value.match(/^[\u0600-\u06FF\s]+$/) ? setError(name, {
                  type: 'custom',
                  message: 'تنها استفاده از حروف فارسی مجاز است'
                }) : clearErrors(name);
              }
              onBlur()
            }}
            type={type === 'otp' ? 'tel' : type}
            error={!!error}
            helperText={
              error ? error.message : ''
            }
            dir='rtl'
            inputRef={ref}
            InputProps={{ classes: { input: "!text-sm" } }}
            FormHelperTextProps={{ classes: "!text-sm", className: "!text-xs text-right" }}
            InputLabelProps={{ classes: "!text-xs", dir: 'rtl' }}
          />
        )
      }}
    />
  )
}
